import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ScheduledJobStatusResponse } from '~/features/content-enrichment/types/scheduled-enrichment.types';

interface Props {
  /** Scheduled-enrichment aggregate status for this batch — `undefined` until the
   *  gated query resolves, and only fetched for catalog-enrichment-funnel batches. */
  status: ScheduledJobStatusResponse | undefined;
}

/** Preview kinds this banner surfaces — the pipeline failure is already reflected
 *  by the parent status chip, so it is deliberately excluded here. */
const PREVIEW_KINDS = ['contentScoring', 'sampleEnrichment'] as const;

/**
 * Warns that a scheduled run's preview stage FAILED even though the batch's parent
 * status rolled up to COMPLETED/delivered (a delivered pipeline wins the rollup, so
 * a stale-source preview failure is otherwise invisible). Reads the per-kind
 * `failureReason` from the aggregate endpoint — the parent DTO never carries it.
 * Renders nothing when no preview kind failed.
 */
export function ScheduledPreviewFailureBanner({ status }: Props) {
  const { t } = useTranslation('catalog-operations');

  const failures = PREVIEW_KINDS.map((kind) => ({ kind, block: status?.[kind] })).filter(
    (f) => f.block?.status === 'FAILED',
  );

  if (failures.length === 0) return null;

  return (
    <div
      data-testid="batch-detail-preview-failure-banner"
      className="border-warning/30 bg-warning-50 flex gap-3 rounded-xl border p-4"
    >
      <div className="text-warning flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50">
        <AlertTriangle className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0 space-y-2">
        <div className="space-y-0.5">
          <h3 className="text-foreground text-sm leading-5 font-semibold">
            {t('previewFailure.title')}
          </h3>
          <p className="text-muted-foreground text-sm leading-5">{t('previewFailure.subtitle')}</p>
        </div>
        <ul className="space-y-1.5">
          {failures.map(({ kind, block }) => (
            <li
              key={kind}
              data-testid={`batch-detail-preview-failure-item-${kind}`}
              className="text-sm leading-5"
            >
              <span className="text-foreground font-semibold">
                {t(`previewFailure.kind.${kind}`)}
              </span>
              {/* Backend reason is human-readable, operator-facing text — render as-is. */}
              {block?.failureReason ? (
                <span className="text-muted-foreground">: {block.failureReason}</span>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
